import { adminApiClient } from './adminApi';

// Analytics DTOs
export interface DashboardAnalyticsDto {
  monthlyTrends: MonthlyTrendDto[];
  topCategories: TopCategoryDto[];
  geographicDistribution: GeographicDataDto[];
  priceAnalysis: PriceAnalysisDto;
}

export interface OverviewStatsDto {
  totalUsers: number;
  totalAds: number;
  activeAds: number;
  totalPayments: number;
  totalRevenue: number;
  newUsersToday: number;
  newAdsToday: number;
  revenueToday: number;
  newUsersThisMonth: number;
  newAdsThisMonth: number;
  revenueThisMonth: number;
  userGrowthRate: number;
  adGrowthRate: number;
  revenueGrowthRate: number;
}

export interface DateValueDto {
  date: string;
  count: number;
  value: number;
  label: string;
}

export interface CategoryStatsDto {
  categoryId: string;
  categoryName: string;
  adCount: number;
  activeAdCount: number;
  totalValue: number;
  percentage: number;
}

export interface ProvinceStatsDto {
  provinceName: string;
  userCount: number;
  adCount: number;
  totalRevenue: number;
}

export interface AdStatusStatsDto {
  status: string;
  count: number;
  percentage: number;
}

export interface UserTypeStatsDto {
  userType: string;
  count: number;
  percentage: number;
}

export interface PromotionPackageStatsDto {
  packageId: string;
  packageName: string;
  purchaseCount: number;
  totalRevenue: number;
  activeSubscriptions: number;
}

export interface MonthlyTrendDto {
  month: string;
  users: number;
  ads: number;
  revenue: number;
}

export interface TopCategoryDto {
  categoryId: string;
  categoryName: string;
  adCount: number;
  averagePrice: number;
  totalViews: number;
}

export interface GeographicDataDto {
  province: string;
  adCount: number;
  userCount: number;
}

export interface PriceAnalysisDto {
  averagePrice: number;
  medianPrice: number;
  priceRanges: PriceRangeDto[];
}

export interface PriceRangeDto {
  range: string;
  count: number;
}

// Analytics API service
export const analyticsApi = {
  // Get complete dashboard analytics
  getDashboardAnalytics: async (fromDate?: string, toDate?: string): Promise<DashboardAnalyticsDto> => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const queryString = params.toString();
    const url = queryString ? `/admin/dashboard/analytics?${queryString}` : '/admin/dashboard/analytics';
    
    const response = await adminApiClient.get(url);
    return response.data;
  },

  // Get overview statistics
  getOverviewStats: async (): Promise<OverviewStatsDto> => {
    const response = await adminApiClient.get('/admin/analytics/overview');
    return response.data;
  },

  // Get user registration trend
  getUserRegistrationTrend: async (fromDate: string, toDate: string, granularity = 'day'): Promise<DateValueDto[]> => {
    const response = await adminApiClient.get('/admin/analytics/users/registration-trend', {
      params: { fromDate, toDate, granularity }
    });
    return response.data;
  },

  // Get ad posting trend
  getAdPostingTrend: async (fromDate: string, toDate: string, granularity = 'day'): Promise<DateValueDto[]> => {
    const response = await adminApiClient.get('/admin/analytics/ads/posting-trend', {
      params: { fromDate, toDate, granularity }
    });
    return response.data;
  },

  // Get revenue trend
  getRevenueTrend: async (fromDate: string, toDate: string, granularity = 'day'): Promise<DateValueDto[]> => {
    const response = await adminApiClient.get('/admin/analytics/revenue/trend', {
      params: { fromDate, toDate, granularity }
    });
    return response.data;
  },

  // Get category statistics
  getCategoryStats: async (): Promise<CategoryStatsDto[]> => {
    const response = await adminApiClient.get('/admin/analytics/categories/stats');
    return response.data;
  },

  // Get geographic distribution
  getGeographicDistribution: async (): Promise<ProvinceStatsDto[]> => {
    const response = await adminApiClient.get('/admin/analytics/geographic/distribution');
    return response.data;
  },

  // Get ad status distribution
  getAdStatusDistribution: async (): Promise<AdStatusStatsDto[]> => {
    const response = await adminApiClient.get('/admin/analytics/ads/status-distribution');
    return response.data;
  },

  // Get user type distribution
  getUserTypeDistribution: async (): Promise<UserTypeStatsDto[]> => {
    const response = await adminApiClient.get('/admin/analytics/users/type-distribution');
    return response.data;
  },

  // Get promotion package statistics
  getPromotionPackageStats: async (): Promise<PromotionPackageStatsDto[]> => {
    const response = await adminApiClient.get('/admin/analytics/packages/stats');
    return response.data;
  }
}; 